/**
 * MessageText
 *
 * Renderiza o texto de uma mensagem do chat com:
 *  - URLs detectadas viram links clicaveis (target=_blank, rel seguro)
 *  - Markdown leve estilo WhatsApp:
 *      *texto*    -> negrito
 *      _texto_    -> italico
 *      ~texto~    -> riscado
 *      `texto`    -> monospace (codigo inline)
 *  - Quebra forcada em URLs/strings longas sem espaco (overflowWrap +
 *    wordBreak) para evitar scroll horizontal no balao da mensagem.
 *
 * Renderiza inline; o componente pai define cor, tamanho de fonte, etc.
 * Preservacao de \n e espacos depende de white-space: pre-wrap no pai.
 *
 * Implementacao: usa `String.prototype.matchAll()` em vez de `regex.exec()`
 * em loop. matchAll cria um iterador isolado e nao mutua o `lastIndex` da
 * instancia do RegExp, o que evita um bug de recursao infinita quando as
 * formatacoes sao aninhadas (ex: `*texto*` chama o parser de novo para
 * processar o conteudo interno, e o reset de lastIndex no parser interno
 * fazia o parser externo re-encontrar o mesmo match e travar a UI).
 */
import React from 'react';

// URLs: aceita http(s)://... e www.algo.algo[...]. Mantemos curto para evitar
// matches espurios em texto natural.
const URL_REGEX = /(\bhttps?:\/\/[^\s<>"']+|\bwww\.[a-z0-9-]+(?:\.[a-z0-9-]+)+[^\s<>"']*)/gi;

// Markdown WhatsApp — exige conteudo nao vazio e sem o delimitador no meio
// da mesma linha. Non-greedy para nao casar com varios delimitadores na linha.
const BOLD_REGEX = /\*([^*\n]+?)\*/g;
const ITALIC_REGEX = /_([^_\n]+?)_/g;
const STRIKE_REGEX = /~([^~\n]+?)~/g;
const CODE_REGEX = /`([^`\n]+?)`/g;

const linkStyle = {
  color: 'inherit',
  textDecoration: 'underline',
  wordBreak: 'break-all',
  overflowWrap: 'anywhere',
};

const codeStyle = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: '0.92em',
  backgroundColor: 'rgba(127,127,127,0.18)',
  padding: '0 4px',
  borderRadius: 4,
};

/**
 * Aplica `regex` em cada piece-string e substitui matches pelo node retornado
 * por `renderMatch`. Nodes (filhos ja React) sao preservados intactos.
 * Usa matchAll para nao mutar o lastIndex do RegExp — seguro para chamadas
 * aninhadas/reentrantes.
 */
const tokenize = (pieces, regex, renderMatch) => {
  const out = [];
  let keyCounter = 0;
  for (const piece of pieces) {
    if (typeof piece !== 'string') {
      out.push(piece);
      continue;
    }
    const matches = [...piece.matchAll(regex)];
    if (matches.length === 0) {
      out.push(piece);
      continue;
    }
    let lastIndex = 0;
    for (const match of matches) {
      const start = match.index;
      if (start > lastIndex) out.push(piece.slice(lastIndex, start));
      out.push(renderMatch(match, `t-${keyCounter++}`));
      lastIndex = start + match[0].length;
    }
    if (lastIndex < piece.length) out.push(piece.slice(lastIndex));
  }
  return out;
};

const renderLink = (match, key) => {
  const raw = match[0];
  const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  return (
    <a
      key={key}
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      style={linkStyle}
      onClick={(e) => e.stopPropagation()}
    >
      {raw}
    </a>
  );
};

const renderCode = (match, key) => (
  <code key={key} style={codeStyle}>{match[1]}</code>
);

const renderBold = (match, key) => (
  <strong key={key}>{applyInline(match[1])}</strong>
);
const renderItalic = (match, key) => (
  <em key={key}>{applyInline(match[1])}</em>
);
const renderStrike = (match, key) => (
  <span key={key} style={{ textDecoration: 'line-through' }}>
    {applyInline(match[1])}
  </span>
);

// Apenas formatacoes inline (bold/italic/strike) aninhadas. Code/URL nao
// recursam para nao duplicar trabalho — o pai ja tratou.
function applyInline(text) {
  let pieces = [text];
  pieces = tokenize(pieces, BOLD_REGEX, renderBold);
  pieces = tokenize(pieces, ITALIC_REGEX, renderItalic);
  pieces = tokenize(pieces, STRIKE_REGEX, renderStrike);
  return pieces;
}

const MessageText = ({ text }) => {
  if (text == null) return null;
  const raw = String(text);
  if (!raw) return null;

  // Ordem importa: CODE primeiro (conteudo literal, nao formata por dentro),
  // depois URL, depois bold/italic/strike.
  let pieces = [raw];
  pieces = tokenize(pieces, CODE_REGEX, renderCode);
  pieces = tokenize(pieces, URL_REGEX, renderLink);
  pieces = tokenize(pieces, BOLD_REGEX, renderBold);
  pieces = tokenize(pieces, ITALIC_REGEX, renderItalic);
  pieces = tokenize(pieces, STRIKE_REGEX, renderStrike);

  return (
    <>
      {pieces.map((p, i) =>
        typeof p === 'string' ? <React.Fragment key={`s-${i}`}>{p}</React.Fragment> : p
      )}
    </>
  );
};

export default MessageText;
